import React from 'react';

import { COLUMN_CATEGORY_ORDER, compareSortKeys } from '@aline/common';
import { Paper, Typography } from '@mui/material';
import { groupBy } from 'lodash';
import { useSnackbar } from 'notistack';
import { Apollo } from 'src/gql/generated';
import {
  useAddTriageColumn,
  useCreateColumn,
  useDeleteColumn,
  useMoveColumn,
  useUpdateColumn,
} from 'src/hooks/boards';
import { makeStyles } from 'tss-react/mui';

import { ColumnCategoryGroup } from './ColumnCategoryGroup';
import type { Column } from './helpers';
import { getCategoryLabel } from './helpers';

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  description: {
    color: theme.palette.text.secondary,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(3),
    justifyContent: 'space-between',
  },
  paper: {
    background: '#FFF',
    border: '1px solid #D1D1D1',
    borderRadius: 16,
    padding: theme.spacing(2, 3),
  },
  title: {
    fontWeight: 600,
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface ColumnsStatusSectionProps {
  boardId: string;
  columns: Column[];
  signingColumnId: string | null;
}

export function ColumnsStatusSection({
  boardId,
  columns,
  signingColumnId,
}: ColumnsStatusSectionProps): JSX.Element {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const columnsByCategory = groupBy(columns, column => column.category);
  const hasTriageColumn = columns.some(col => col.category === Apollo.ColumnCategory.Triage);

  const { moveColumn } = useMoveColumn();
  const { updateColumn } = useUpdateColumn();
  const { deleteColumn } = useDeleteColumn();
  const { createColumn } = useCreateColumn(boardId);
  const { addTriageColumn } = useAddTriageColumn(boardId);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.titleContainer}>
          <Typography className={classes.title} variant="subtitle1">
            Board Columns
          </Typography>
          <Typography className={classes.description} variant="body2">
            Customize your Board statuses below
          </Typography>
        </div>
      </div>
      <Paper className={classes.paper} elevation={0}>
        {COLUMN_CATEGORY_ORDER.map(categoryStr => {
          const category = categoryStr;
          const isTriage = category === Apollo.ColumnCategory.Triage;
          const sortedColumns = [...(columnsByCategory[category] ?? [])].sort((a, b) =>
            compareSortKeys(a.sortKey, b.sortKey)
          );

          return (
            <ColumnCategoryGroup
              key={category}
              allColumns={columns}
              allowAdd={!isTriage || !hasTriageColumn}
              allowDrag={!isTriage}
              category={category}
              categoryLabel={getCategoryLabel(category)}
              columns={sortedColumns}
              signingColumnId={signingColumnId}
              onAdd={
                isTriage
                  ? async () => {
                      const { data } = await addTriageColumn();
                      if (data?.addTriageColumn)
                        enqueueSnackbar('Triage column added', { variant: 'success' });
                    }
                  : undefined
              }
              onAddColumn={async newColumn => {
                await createColumn({
                  category: newColumn.category,
                  color: newColumn.color,
                  name: newColumn.name,
                });
              }}
              onDeleteColumn={async (columnId, targetColumnId) => {
                await deleteColumn(columnId, targetColumnId);
              }}
              onMoveColumn={async (columnId, targetColumnId) => {
                await moveColumn(columnId, targetColumnId);
              }}
              onUpdateColumn={async (columnId, props) => {
                await updateColumn(columnId, {
                  category: props.category,
                  color: props.color,
                  name: props.name,
                });
              }}
            />
          );
        })}
      </Paper>
    </div>
  );
}
